import { useMemo } from "react";

/**
 * Mini overlay chart plotting the steel-index vs cement-index side by side.
 * Both series are normalised to % change from the first datapoint so they can
 * share a single scale even though ₹/MT and ₹/bag differ by three orders of
 * magnitude.  Lets buyers spot the swap window at a glance.
 */
export const SpreadChart = ({ steelSeries = [], cementSeries = [], height = 80 }) => {
  const { paths, ymin, ymax } = useMemo(() => {
    const normalise = (series) => {
      if (!series || series.length < 2) return [];
      const base = series[0].price || 1;
      return series.map((p) => ({ date: p.date, v: ((p.price - base) / base) * 100 }));
    };
    const steel = normalise(steelSeries);
    const cement = normalise(cementSeries);
    const all = [...steel, ...cement].map((p) => p.v);
    if (!all.length) return { paths: [], ymin: -1, ymax: 1 };
    const ymin = Math.min(...all, -0.5);
    const ymax = Math.max(...all, 0.5);
    const range = Math.max(0.5, ymax - ymin);
    const W = 360;
    const H = height;
    const pad = 4;
    const build = (arr, color) => {
      if (!arr.length) return null;
      const step = (W - pad * 2) / Math.max(1, arr.length - 1);
      const pts = arr.map((p, i) => {
        const x = pad + i * step;
        const y = pad + (H - pad * 2) - ((p.v - ymin) / range) * (H - pad * 2);
        return { x, y };
      });
      return {
        color,
        d: pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" "),
        end: pts[pts.length - 1],
        change: arr[arr.length - 1].v,
      };
    };
    return {
      paths: [build(steel, "#22D3EE"), build(cement, "#F5A524")].filter(Boolean),
      ymin, ymax,
    };
  }, [steelSeries, cementSeries, height]);

  if (!paths.length) return null;

  return (
    <div data-testid="spread-chart" className="relative">
      <div className="flex items-center justify-between mb-2">
        <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">
          Steel vs Cement · % from start
        </span>
        <div className="flex items-center gap-3 font-grotesk text-[9px] uppercase tracking-[0.18em]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-[2px]" style={{ background: "#22D3EE", boxShadow: "0 0 6px #22D3EE" }} />
            <span style={{ color: "#22D3EE" }}>Steel</span>
            {paths[0] && (
              <span className="text-[#C9C9C2]">{paths[0].change >= 0 ? "+" : ""}{paths[0].change.toFixed(1)}%</span>
            )}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-[2px]" style={{ background: "#F5A524", boxShadow: "0 0 6px #F5A524" }} />
            <span style={{ color: "#F5A524" }}>Cement</span>
            {paths[1] && (
              <span className="text-[#C9C9C2]">{paths[1].change >= 0 ? "+" : ""}{paths[1].change.toFixed(1)}%</span>
            )}
          </span>
        </div>
      </div>
      <svg viewBox={`0 0 360 ${height}`} preserveAspectRatio="none" className="w-full block" style={{ height }}>
        <defs>
          <filter id="spread-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* zero baseline */}
        {(() => {
          const range = Math.max(0.5, ymax - ymin);
          const y0 = 4 + (height - 8) - ((0 - ymin) / range) * (height - 8);
          return <line x1="0" x2="360" y1={y0} y2={y0} stroke="rgba(255,255,255,0.08)" strokeDasharray="2 3" />;
        })()}
        {paths.map((p) => (
          <g key={p.color}>
            <path d={p.d} fill="none" stroke={p.color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#spread-glow)" />
            <circle cx={p.end.x} cy={p.end.y} r="2.6" fill={p.color} />
          </g>
        ))}
      </svg>
    </div>
  );
};
